import { useFormContext, Controller } from 'react-hook-form';
// @mui
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel, {
  FormControlLabelProps,
  formControlLabelClasses,
} from '@mui/material/FormControlLabel';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { intersection } from 'lodash';

// ----------------------------------------------------------------------

interface RHFCheckboxProps extends Omit<FormControlLabelProps, 'control'> {
  name: string;
  helperText?: React.ReactNode;
}

export function RHFCheckbox({ name, helperText, ...other }: RHFCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div>
          <FormControlLabel control={<Checkbox {...field} checked={field.value} />} {...other} />

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </div>
      )}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFMultiCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'label'> {
  name: string;
  options: { label: string; value: any }[];
  row?: boolean;
  label?: string;
  spacing?: number;
  helperText?: React.ReactNode;
  showError?: boolean;
  selectAll?: boolean;
  disabledIds?: string[];
}

export function RHFMultiCheckbox({
  row,
  name,
  label,
  options,
  spacing,
  helperText,
  sx,
  showError = true,
  selectAll,
  disabledIds,
  ...other
}: RHFMultiCheckboxProps) {
  const { control, getValues } = useFormContext();
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [disabledOptions, setDisabledOptions] = useState<any>([]);
  useEffect(() => {
    if (disabledIds) {
      const optionIds = options.map((option) => option.value);
      setDisabledOptions(intersection(disabledIds, optionIds));
    }
  }, [disabledIds]);
  useEffect(() => {
    const value = getValues(name);
    if (value) {
      const optionIds = options.map((option) => option.value);
      setSelectedOptions(intersection(value, optionIds));
    }
  }, [getValues(name)]);
  const getSelected = (selectedItems: string[], item: string) => {
    // console.log('-------------------------');
    if (disabledIds?.includes(item)) return selectedItems;
    const index = selectedItems.findIndex((select) => select === item);
    // console.log('get selected', index, item, selectedItems);
    if (index !== -1) {
      const updated = [...selectedItems];
      // console.log('get selected before update', updated);
      updated.splice(index, 1);
      const newOptions = [...selectedOptions].filter((prevItem: any) => prevItem !== item);
      setSelectedOptions(newOptions);
      // console.log('get selected after update', updated, newOptions);
      return [...updated];
    }
    const newOptions = [...selectedOptions, item];
    setSelectedOptions(newOptions);
    // console.log('new selected options', newOptions, [...selectedItems, ...newOptions]);
    return [...selectedItems, item];
  };

  const onSelectAllRows = (checked: boolean, selectedItems: string[]) => {
    // console.log('-------------------------');

    const dataInThis = options.map((option) => option.value);
    // console.log('dataInThis', dataInThis, checked);
    if (checked) {
      const updatedSelection: string[] = [];
      // console.log('update before selected', updatedSelection);
      dataInThis.forEach((item) => {
        if (disabledIds?.includes(item)) return;
        if (!selectedItems.includes(item)) updatedSelection.push(item);
      });
      setSelectedOptions(dataInThis);
      // console.log('after selected', updatedSelection, selectedOptions, [
      //   ...selectedItems,
      //   ...updatedSelection,
      // ]);
      return [...selectedItems, ...updatedSelection];
    }
    const updatedSelection = [...selectedItems];
    dataInThis.forEach((item) => {
      if (disabledIds?.includes(item)) return;
      const index = updatedSelection.findIndex((value) => value === item);
      // console.log('index remove ', index, item);
      // console.log('before remove ', updatedSelection);

      if (index !== -1) updatedSelection.splice(index, 1);
      // console.log('after remove ', updatedSelection);
    });
    setSelectedOptions(updatedSelection);
    return updatedSelection;
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl component="fieldset">
          {label && !selectAll && <Typography variant="subtitle2">{label}</Typography>}

          {label && !!selectAll && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={disabledOptions.length === options.length}
                    checked={selectedOptions.length === options.length}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(onSelectAllRows(event.target.checked, field.value))
                    }
                  />
                }
                label={<Typography variant="subtitle2">{label}</Typography>}
              />
            </>
          )}

          <FormGroup
            sx={{
              ...(row && {
                flexDirection: 'row',
              }),
              [`& .${formControlLabelClasses.root}`]: {
                '&:not(:last-of-type)': {
                  mb: spacing || 0,
                },
                ...(row && {
                  mr: 0,
                  '&:not(:last-of-type)': {
                    mr: spacing || 2,
                  },
                }),
              },
              ...sx,
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    disabled={disabledOptions.includes(option.value)}
                    checked={field.value.includes(option.value)}
                    onChange={() => field.onChange(getSelected(field.value, option.value))}
                  />
                }
                label={option.label}
                {...other}
              />
            ))}
          </FormGroup>

          {showError && (!!error || helperText) && (
            <FormHelperText error={!!error} sx={{ mx: 0 }}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
